import { ReactNode, useLayoutEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConfigProvider, theme as antdTheme } from 'antd'
import enUS from 'antd/locale/en_US'
import arEG from 'antd/locale/ar_EG'
import { useAppStore } from '@/app/store'
import { useTranslation } from 'react-i18next'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
})

function HtmlThemeSync() {
  const colorMode = useAppStore((s) => s.theme)

  useLayoutEffect(() => {
    if (colorMode === 'dark') document.documentElement.classList.add('dark')
    else document.documentElement.classList.remove('dark')
  }, [colorMode])

  return null
}

function AntdThemeProvider({ children }: { children: ReactNode }) {
  const colorMode = useAppStore((s) => s.theme)
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('ar') ? arEG : enUS

  return (
    <ConfigProvider
      direction={i18n.language?.startsWith('ar') ? 'rtl' : 'ltr'}
      locale={locale}
      theme={{
        algorithm: colorMode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#dc2925',
          borderRadius: 8,
        },
      }}
    >
      <HtmlThemeSync />
      {children}
    </ConfigProvider>
  )
}

interface AppProvidersProps {
  children: ReactNode
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AntdThemeProvider>
        <BrowserRouter>{children}</BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </AntdThemeProvider>
    </QueryClientProvider>
  )
}
